import { pickDisplay, type UnitDef } from '#core/lib/units/registry.ts';
import type { Unit } from '#core/lib/units/unit.ts';

/**
 * Format hints for rendering a numeric column.
 *
 * Returned by {@link inferNumberFormat} so the column renderer (e.g. NumberCell)
 * can pick a decimal scale AND decide whether a thousand separator is appropriate.
 *
 * The two flags are independent: a year column wants `scale: 0` *and*
 * `useThousandSeparator: false` (so 2025 doesn't render as "2,025"), while a
 * count column also wants `scale: 0` but with the separator (so 12000 reads as
 * "12,000").
 */
export interface NumberFormatHints {
  /** Fixed number of decimal places to display. Omit to render the value's natural precision. */
  scale?: number;
  /** Whether to render a thousand separator (e.g. comma). */
  useThousandSeparator: boolean;
}
/**
 * Infers display formatting for a column of numeric values.
 *
 * Decision tree:
 * 1. All zeros / empty → no decimals, no separator.
 * 2. **Year-like** — all integers in 1000–9999 (and accessor name matches `/year/i`
 *    when provided) — 0 decimals, no separator. Avoids "2,025" for the year 2025.
 * 3. **Integer-only** — 0 decimals; separator only when magnitude ≥ 1000 (so
 *    counts like 12000 render as "12,000" but months 1–12 render as plain "8").
 * 4. **Mixed/decimal** — magnitude bands map to a fixed scale:
 *    - ≥1000 → 0 decimals (4248 → "4,248")
 *    - ≥100  → 1 decimal  (486.9)
 *    - ≥1    → 2 decimals (56.54)
 *    - ≥0.01 → 3 decimals (0.428)
 *    - <0.01 → 4 decimals (0.0042)
 *
 * The accessor parameter is optional. When provided it tightens the year-like
 * heuristic — purely numeric columns named e.g. "wellId" with values in the
 * 1000–9999 range stay as plain integers, not years.
 */
export function inferNumberFormat(values: number[], accessor?: string): NumberFormatHints {
  const nonZero = values.filter((v) => v !== 0);

  if (nonZero.length === 0) {
    return { scale: 0, useThousandSeparator: false };
  }

  const allInteger = values.every((v) => Number.isFinite(v) && Number.isInteger(v));
  const maxAbs = Math.max(...nonZero.map((v) => Math.abs(v)));

  // Year-like columns: 4-digit integers in plausible year range.
  // When an accessor is supplied we additionally require its name to mention
  // "year" — keeps unrelated 4-digit-id columns from being treated as years.
  const isYearLike =
    allInteger && nonZero.every((v) => v >= 1000 && v <= 9999) && (accessor === undefined || /year/i.test(accessor));

  if (isYearLike) {
    return { scale: 0, useThousandSeparator: false };
  }

  if (allInteger) {
    return { scale: 0, useThousandSeparator: maxAbs >= 1000 };
  }

  if (maxAbs >= 1000) {
    return { scale: 0, useThousandSeparator: true };
  }
  if (maxAbs >= 100) {
    return { scale: 1, useThousandSeparator: false };
  }
  if (maxAbs >= 1) {
    return { scale: 2, useThousandSeparator: false };
  }
  if (maxAbs >= 0.01) {
    return { scale: 3, useThousandSeparator: false };
  }
  return { scale: 4, useThousandSeparator: false };
}
/**
 * Formats a numeric value using {@link NumberFormatHints}.
 *
 * Canonical number→string formatter used by both table cells and chart
 * tooltips / axis ticks so display stays consistent across the dashboard.
 */
export function formatNumber(value: number, hints: NumberFormatHints): string {
  const options: Intl.NumberFormatOptions = { useGrouping: hints.useThousandSeparator };
  if (hints.scale !== undefined) {
    options.minimumFractionDigits = hints.scale;
    options.maximumFractionDigits = hints.scale;
  }
  return new Intl.NumberFormat('en-US', options).format(value);
}
/**
 * Thousand-separated format hints for an already-extracted numeric column.
 *
 * SSOT for the recurring `{ ...inferNumberFormat(values), useThousandSeparator:
 * true }` pattern used by table number cells and chart axis/tooltip ticks —
 * inference picks the scale, the thousand separator is always on.
 */
export function numberHints(values: number[]): NumberFormatHints {
  return { ...inferNumberFormat(values), useThousandSeparator: true };
}
/**
 * Per-tick adaptive formatter for value axes.
 *
 * Each tick is formatted by its own magnitude — inference runs over the single
 * value — so a large-spanning axis keeps small-tick decimals (0.42 stays
 * "0.420") while big ticks keep their thousand separator and integer form
 * (4248 → "4,248") on the same axis.
 */
export const formatTick = (value: number): string => formatNumber(value, numberHints([value]));

const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

// Laddered-mantissa formatter: three significant figures (trailing zeros dropped)
// with grouping, so a rung value reads clean for typical magnitudes ("4.2", "31.3",
// "600") yet a small member of a fixed-rung column keeps meaningful digits instead
// of rounding to "0" ("0.04", "0.0042"). The petroleum prefix carries the
// magnitude, so no `Intl` compact notation here.
const MANTISSA = new Intl.NumberFormat('en-US', { useGrouping: true, maximumSignificantDigits: 3 });

/**
 * Renders a number in compact notation — `1.2k`, `3.4M`. SSOT for the
 * `compact` DisplayFormat across every panel kind (KPI metric tile,
 * table column cell).
 */
export const formatCompact = (value: number): string => COMPACT.format(value);

/**
 * Renders a number as a rounded integer with locale thousand separators
 * — `1,235`. SSOT for the `integer` DisplayFormat across every panel
 * kind (KPI metric tile, table column cell).
 */
export const formatInteger = (value: number): string => Math.round(value).toLocaleString('en');
/**
 * Renders an absolute percentage number — `42` → `42.0%`, `1,234.5` → `1,234.5%`.
 * SSOT for the `percent` DisplayFormat across every panel kind (KPI metric tile,
 * table column cell): percent values are stored absolute (0-100), so the number is
 * appended with `%` and never rescaled. Routes through {@link formatNumber} so the
 * `en-US` locale and grouping/scale are deterministic (grouping on, one fixed decimal).
 */
export const formatPercent = (value: number): string =>
  `${formatNumber(value, { useThousandSeparator: true, scale: 1 })}%`;
/**
 * Renders a number as USD currency — `$1,234.50`. SSOT for the `currency`
 * DisplayFormat across every panel kind (KPI metric tile, table column
 * cell). Routes through {@link formatNumber} so the `en-US` locale and
 * grouping/scale are deterministic (grouping on, two fixed decimals).
 */
export const formatCurrency = (value: number): string =>
  `$${formatNumber(value, { useThousandSeparator: true, scale: 2 })}`;

/**
 * The scaled mantissa plus laddered unit label chosen for a canonical-base value.
 *
 * `value` is the value rescaled onto the chosen ladder rung; `unit` is that
 * rung's display string (e.g. `MMSCF/d`); `def` is the full {@link UnitDef} the
 * caller can reuse (e.g. to force the same rung across a group via `rung`).
 */
export interface UnitDisplay {
  /** Value rescaled onto the chosen rung — the displayed mantissa. */
  readonly value: number;
  /** Laddered unit label of the chosen rung (e.g. `MMSCF/d`). */
  readonly unit: string;
  /** The {@link UnitDef} of the chosen rung. */
  readonly def: UnitDef;
}

/**
 * Unit-aware compact display of a canonical-base value.
 *
 * This path OWNS its scale selection through {@link pickDisplay}: magnitude
 * compaction is folded into the petroleum unit prefix (M / MM / B / T) rather
 * than the locale `compact` notation, so the unit-aware path NEVER emits an
 * `Intl` `'k'`/`'M'` suffix — `formatCompact` is the unit-less SSOT and is left
 * untouched.
 *
 * - **auto** (no `rung`) — `pickDisplay` walks the dimension's ladder and picks
 *   the rung whose mantissa lands in `[1, 1000)`, clamped at the ladder ends.
 * - **fixed** (`rung` supplied) — the caller-chosen rung is forced, so values
 *   across a group (chart axis, table column, related-KPI trio) stay comparable.
 *
 * Returns the raw rescaled `value` (`chosen.fromBase(vBase)`) and the rung; the
 * mantissa is rendered to at most one decimal by {@link formatUnitMantissa}.
 */
export function pickUnitDisplay(vBase: number, dimensionOrUnit: Unit | UnitDef, rung?: UnitDef['slug']): UnitDisplay {
  const def = pickDisplay(vBase, dimensionOrUnit, rung === undefined ? undefined : { rung });
  return { value: def.fromBase(vBase), unit: def.display, def };
}

/**
 * Renders a canonical-base value as `mantissa unit` using the unit-aware
 * compact path — e.g. `4_200_000` scf/d → `4.2 MMSCF/d`. The mantissa is
 * laddered (NOT `Intl` compact), so no `'k'`/`'M'` suffix is emitted.
 *
 * `rung` forces a fixed ladder rung (chart axis / table column / KPI trio);
 * omit it for the auto (standalone KPI) mode.
 */
export function formatUnitCompact(vBase: number, dimensionOrUnit: Unit | UnitDef, rung?: UnitDef['slug']): string {
  const { unit } = pickUnitDisplay(vBase, dimensionOrUnit, rung);
  const mantissa = formatUnitMantissa(vBase, dimensionOrUnit, rung);
  return unit === '' ? mantissa : `${mantissa} ${unit}`;
}

/**
 * The scaled mantissa string for a canonical-base value on its chosen ladder rung,
 * WITHOUT the unit label appended — for render sites that carry the laddered unit
 * separately (chart axis title, table header, KPI unit slot) and scale each value
 * (tick/cell) onto a shared, fixed `rung`. Mirrors {@link formatUnitCompact} minus
 * the unit so the mantissa formatting stays a single SSOT.
 */
export function formatUnitMantissa(vBase: number, dimensionOrUnit: Unit | UnitDef, rung?: UnitDef['slug']): string {
  const { value } = pickUnitDisplay(vBase, dimensionOrUnit, rung);
  return MANTISSA.format(value);
}
